import React, { useEffect, useRef, useState } from 'react';
import {
  Box,
  RadioGroup,
  FormControlLabel,
  Radio,
  TextField,
  Stack,
  Typography,
} from '@mui/material';
import Modal from './Modal';
import Button from './Button';
import Alert from './Alert';
import api from '../services/api';
import { localInputFromUtc, localNow, toUtcNaive } from '../utils/fixedAt';

function MarkFixedModal({ isOpen, onClose, anomaly, onMarkedFixed }) {
  const [reextractionPerformed, setReextractionPerformed] = useState('no');
  const [reextractionFrom, setReextractionFrom] = useState('');
  const [reextractionTo, setReextractionTo] = useState('');
  const [fixedAt, setFixedAt] = useState(localNow);
  // True while fixedAt is the heal PR's merge time (DPS-4722); any edit clears it.
  const [fromPrMerge, setFromPrMerge] = useState(false);
  const editedRef = useRef(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const anomalyId = anomaly?.id;
  useEffect(() => {
    if (!isOpen || !anomalyId) return undefined;
    // Start every open from a clean form: after a successful submit the parent
    // closes the modal without handleClose, so nothing else resets it.
    let cancelled = false;
    editedRef.current = false;
    setReextractionPerformed('no');
    setReextractionFrom('');
    setReextractionTo('');
    setFixedAt(localNow());
    setFromPrMerge(false);
    setError(null);
    setSubmitting(false);
    api.getAnomalyFixDefault(anomalyId)
      .then((res) => {
        const local = res?.source === 'pr_merge' ? localInputFromUtc(res.fixed_at) : null;
        if (cancelled || !local || editedRef.current) return;
        setFixedAt(local);
        setFromPrMerge(true);
      })
      .catch(() => {}); // no suggestion: keep "now"
    return () => { cancelled = true; };
  }, [isOpen, anomalyId]);

  const handleFixedAtChange = (e) => {
    editedRef.current = true;
    setFixedAt(e.target.value);
    setFromPrMerge(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);

    const performed = reextractionPerformed === 'yes';

    if (!fixedAt) {
      setError('Please provide the date and time the anomaly was fixed.');
      setSubmitting(false);
      return;
    }

    if (new Date(fixedAt) > new Date()) {
      setError('Fixed at cannot be in the future.');
      setSubmitting(false);
      return;
    }

    if (performed && (!reextractionFrom || !reextractionTo)) {
      setError('Please provide both From and To dates for reextraction.');
      setSubmitting(false);
      return;
    }

    if (performed && reextractionFrom > reextractionTo) {
      setError('From date must be before To date.');
      setSubmitting(false);
      return;
    }

    try {
      const payload = {
        reextraction_performed: performed,
        reextraction_from: performed ? reextractionFrom : null,
        reextraction_to: performed ? reextractionTo : null,
        fixed_at: toUtcNaive(fixedAt),
      };

      await api.markAnomalyFixed(anomaly.id, payload);
      onMarkedFixed();
    } catch (err) {
      setError(err.message || 'Failed to mark anomaly as fixed');
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    setReextractionPerformed('no');
    setReextractionFrom('');
    setReextractionTo('');
    setFixedAt(localNow());
    setFromPrMerge(false);
    setError(null);
    setSubmitting(false);
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="Mark Anomaly as Fixed">
      <form onSubmit={handleSubmit}>
        <Stack spacing={3} sx={{ mt: 1 }}>
          {error && <Alert variant="error">{error}</Alert>}

          <Typography sx={{ color: '#cbd5e1', fontSize: '0.9rem' }}>
            <strong>{anomaly?.retailer}</strong> &mdash; {anomaly?.attribute} ({anomaly?.page_type})
          </Typography>

          <TextField
            label="Fixed at"
            type="datetime-local"
            value={fixedAt}
            onChange={handleFixedAtChange}
            InputLabelProps={{ shrink: true }}
            inputProps={{ max: localNow() }}
            fullWidth
            required
            helperText={(
              <>
                When the fix was actually applied.
                {fromPrMerge && (
                  <Box component="span" sx={{ ml: 1, px: 0.75, borderRadius: '6px', background: 'rgba(99,102,241,0.2)', color: '#c7d2fe' }}>
                    from PR merge
                  </Box>
                )}
              </>
            )}
            sx={{
              '& .MuiInputBase-root': { color: '#e2e8f0', background: '#0f172a', borderRadius: '8px' },
              '& .MuiInputLabel-root': { color: '#94a3b8' },
              '& .MuiFormHelperText-root': { color: '#94a3b8' },
              '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.2)' },
              '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#6366f1' },
            }}
          />

          <div>
            <Typography sx={{ color: '#e2e8f0', mb: 1, fontWeight: 500 }}>
              Was reextraction performed?
            </Typography>
            <RadioGroup
              value={reextractionPerformed}
              onChange={(e) => setReextractionPerformed(e.target.value)}
              row
            >
              <FormControlLabel
                value="yes"
                control={<Radio sx={{ color: '#6366f1', '&.Mui-checked': { color: '#6366f1' } }} />}
                label={<span style={{ color: '#e2e8f0' }}>Yes</span>}
              />
              <FormControlLabel
                value="no"
                control={<Radio sx={{ color: '#6366f1', '&.Mui-checked': { color: '#6366f1' } }} />}
                label={<span style={{ color: '#e2e8f0' }}>No</span>}
              />
            </RadioGroup>
          </div>

          {reextractionPerformed === 'yes' && (
            <Stack direction="row" spacing={2}>
              <TextField
                label="From"
                type="date"
                value={reextractionFrom}
                onChange={(e) => setReextractionFrom(e.target.value)}
                InputLabelProps={{ shrink: true }}
                fullWidth
                required
                sx={{
                  '& .MuiInputBase-root': { color: '#e2e8f0', background: '#0f172a', borderRadius: '8px' },
                  '& .MuiInputLabel-root': { color: '#94a3b8' },
                  '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.2)' },
                  '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#6366f1' },
                }}
              />
              <TextField
                label="To"
                type="date"
                value={reextractionTo}
                onChange={(e) => setReextractionTo(e.target.value)}
                InputLabelProps={{ shrink: true }}
                fullWidth
                required
                sx={{
                  '& .MuiInputBase-root': { color: '#e2e8f0', background: '#0f172a', borderRadius: '8px' },
                  '& .MuiInputLabel-root': { color: '#94a3b8' },
                  '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.2)' },
                  '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#6366f1' },
                }}
              />
            </Stack>
          )}

          <Stack direction="row" spacing={2} justifyContent="flex-end" sx={{ mt: 2 }}>
            <Button variant="outlined" onClick={handleClose} disabled={submitting}>
              Cancel
            </Button>
            <Button type="submit" disabled={submitting}>
              {submitting ? 'Marking...' : 'Mark as Fixed'}
            </Button>
          </Stack>
        </Stack>
      </form>
    </Modal>
  );
}

export default MarkFixedModal;
